import React, { useState, useEffect, useCallback } from "react";
import { Button, Select, Tag, Switch, Space } from "antd";
import { PlusOutlined, TagsOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getTickets, getUser } from "../network/api";
import AddTicket from "./Modals/addTicket";
import PageHeader from "./common/PageHeader";
import InfoHint from "./common/InfoHint";
import DataTable from "./common/DataTable";
import { notify, serverError } from "../utils/notify";
import {
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TAG_COLOR,
  TICKET_STATUS_OPTIONS,
  TICKET_PRIORITY_LABEL,
  TICKET_PRIORITY_TAG_COLOR,
  TICKET_PRIORITY_OPTIONS,
  TICKET_TYPE_LABEL,
  TICKET_TYPE_TAG_COLOR,
  TICKET_TYPE_OPTIONS,
} from "../constants/tickets";
import { relativeTime } from "../constants/health";
import { rankOf, ROLES } from "../constants/roles";

const HARDWARE_ADMIN_RANK = rankOf(ROLES.HARDWARE_ADMIN);

const Tickets = () => {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);
  const [visible, setVisible] = useState(false);

  // Server-side filters wired to the query params (search is handled client-side
  // by DataTable since the list is small/already loaded).
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || undefined);
  const [priorityFilter, setPriorityFilter] = useState();
  const [typeFilter, setTypeFilter] = useState();
  const [mine, setMine] = useState(searchParams.get("mine") === "true");
  const [unassigned, setUnassigned] = useState(searchParams.get("unassigned") === "true");

  const fetchRole = useCallback(async () => {
    // Seed from cache, then refresh from the API (mirrors sider.js).
    const cached = localStorage.getItem("user");
    if (cached) {
      try {
        setCurrentRole(JSON.parse(cached)?.role || null);
      } catch (e) {
        // ignore malformed cache
      }
    }
    try {
      const user = await getUser();
      setCurrentRole(user?.role || null);
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      const response = await getTickets({
        status: statusFilter,
        priority: priorityFilter,
        type: typeFilter,
        mine: mine || undefined,
        unassigned: unassigned || undefined,
      });
      const formatted = (response.tickets || []).map((t) => ({
        key: t._id,
        _id: t._id,
        deviceId: t.deviceId,
        title: t.title,
        type: t.type,
        priority: t.priority,
        status: t.status,
        assignedTo: t.assignedTo,
        openedAt: t.openedAt,
      }));
      setTickets(formatted);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      notify.error(serverError(error, "Failed to load tickets."));
    }
  }, [statusFilter, priorityFilter, typeFilter, mine, unassigned]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const canCreate = rankOf(currentRole) >= HARDWARE_ADMIN_RANK;

  const openTicket = (id) => {
    navigate(`/dashboard/tickets/${id}`);
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Device ID",
      dataIndex: "deviceId",
      key: "deviceId",
      sorter: (a, b) => (a.deviceId || "").localeCompare(b.deviceId || ""),
    },
    {
      title: <span>Type<InfoHint title="What kind of issue this is (e.g. hardware, connectivity, camera). Helps route it to the right person." /></span>,
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={TICKET_TYPE_TAG_COLOR[type] || "default"}>
          {TICKET_TYPE_LABEL[type] || type}
        </Tag>
      ),
    },
    {
      title: <span>Priority<InfoHint title="How urgent the ticket is. Higher priority should be worked first." /></span>,
      dataIndex: "priority",
      key: "priority",
      render: (priority) => (
        <Tag color={TICKET_PRIORITY_TAG_COLOR[priority] || "default"}>
          {TICKET_PRIORITY_LABEL[priority] || priority}
        </Tag>
      ),
    },
    {
      title: <span>Status<InfoHint title="Where the ticket is in its lifecycle: open → assigned → in progress → resolved → closed." /></span>,
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={TICKET_STATUS_TAG_COLOR[status] || "default"}>
          {TICKET_STATUS_LABEL[status] || status}
        </Tag>
      ),
    },
    {
      title: <span>Assignee<InfoHint title="The technician responsible for this ticket. “—” means it is unassigned and needs picking up." /></span>,
      dataIndex: "assignedTo",
      key: "assignedTo",
      render: (assignedTo) => assignedTo?.name || "—",
    },
    {
      title: "Opened",
      dataIndex: "openedAt",
      key: "openedAt",
      sorter: (a, b) => new Date(a.openedAt || 0) - new Date(b.openedAt || 0),
      render: (openedAt) => relativeTime(openedAt),
    },
  ];

  const toolbar = (
    <>
      <Select
        placeholder="Filter by status"
        allowClear
        style={{ width: 180 }}
        value={statusFilter}
        options={TICKET_STATUS_OPTIONS}
        onChange={(value) => setStatusFilter(value)}
      />
      <Select
        placeholder="Filter by priority"
        allowClear
        style={{ width: 160 }}
        value={priorityFilter}
        options={TICKET_PRIORITY_OPTIONS}
        onChange={(value) => setPriorityFilter(value)}
      />
      <Select
        placeholder="Filter by type"
        allowClear
        style={{ width: 180 }}
        value={typeFilter}
        options={TICKET_TYPE_OPTIONS}
        onChange={(value) => setTypeFilter(value)}
      />
      <Space size={6}>
        <Switch
          checked={mine}
          onChange={setMine}
          checkedChildren="My tickets"
          unCheckedChildren="All"
        />
      </Space>
      <Space size={6}>
        <Switch checked={unassigned} onChange={setUnassigned} checkedChildren="Unassigned" unCheckedChildren="Any assignee" />
      </Space>
    </>
  );

  return (
    <div>
      <PageHeader
        title="Tickets"
        subtitle="Track and resolve device issues"
        tooltip="Repair/maintenance tickets raised against devices — manually or from a health alert. Use the filters and the My/Unassigned toggles to narrow the list; click a row to open the ticket."
        icon={<TagsOutlined />}
        extra={
          canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setVisible(true)}
            >
              New Ticket
            </Button>
          )
        }
      />

      <DataTable
        rowKey="_id"
        columns={columns}
        dataSource={tickets}
        searchableKeys={["title", "deviceId"]}
        searchPlaceholder="Search by device ID or title"
        toolbar={toolbar}
        scrollX={1000}
        onRow={(record) => ({
          onClick: () => openTicket(record._id),
          style: { cursor: "pointer" },
        })}
      />

      <AddTicket
        visible={visible}
        onClose={() => setVisible(false)}
        onSubmit={fetchTickets}
      />
    </div>
  );
};

export default Tickets;
